import { Link, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { PartsHierarchyMenu } from "./PartsHierarchyMenu";
import { Separator } from "@/components/ui/separator";
import { getNavIcon } from "@/lib/navigation-icons";
import { useMainNavigation, type NavigationGroup } from "@/hooks/useMainNavigation";
import { useFeatureFlags } from "@/contexts/FeatureFlagsContext";
import { Skeleton } from "@/components/ui/skeleton";

const partsDropdownItems = [
  { name: "Quick Order", path: "/quick-order", tag: "QuickOrder" },
  { name: "Unknown Part Request", path: "/unknown-part-request", tag: "UnknownPartRequest" },
];

interface DropdownItem {
  name: string;
  path: string;
  tag: string;
}

interface DropdownMenuProps {
  title: string;
  items?: DropdownItem[];
  location: ReturnType<typeof useLocation>;
  isPartsMenu?: boolean;
}

function useGraceConfig() {
  const { flags } = useFeatureFlags();
  const mode = flags.partsMenuGracePeriod;
  const isSticky = mode === 'sticky';
  const graceMs = mode === 'sticky' ? 0 : mode === 'big' ? 400 : mode === 'extended' ? 800 : 150;
  return { isSticky, graceMs };
}

function DropdownMenu({ title, items, location, isPartsMenu }: DropdownMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isSticky: globalSticky, graceMs } = useGraceConfig();

  const isSticky = globalSticky && isPartsMenu;

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setHasInteracted(false);
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (isSticky && hasInteracted) return;
    timeoutRef.current = setTimeout(() => closeMenu(), graceMs);
  };

  useEffect(() => {
    if (!isSticky || !isOpen || !hasInteracted) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) closeMenu();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isSticky, isOpen, hasInteracted, closeMenu]);

  return (
    <div ref={containerRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors duration-200 hover:text-primary rounded-md">
        {title}
        <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", isOpen && "rotate-180")} />
      </button>
      {isOpen && (
        <div
          className="absolute top-full left-0 bg-card backdrop-blur-sm shadow-lg border border-border/20 rounded-lg z-50 animate-fade-in"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {isPartsMenu ? (
            <>
              <PartsHierarchyMenu onClose={closeMenu} onInteraction={() => setHasInteracted(true)} />
              <Separator />
              <div className="p-2">
                {partsDropdownItems.map((item) => {
                  const Icon = getNavIcon(item.tag);
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={closeMenu}
                      className={cn(
                        "flex items-center gap-3 rounded-lg p-3 text-sm font-medium transition-all duration-200 hover:text-primary hover:scale-[1.02] group",
                        location.pathname === item.path && "text-primary font-medium bg-primary/5"
                      )}
                    >
                      <Icon className="h-4 w-4 transition-transform group-hover:scale-110" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="p-2 w-64">
              {items?.map((item) => {
                const Icon = getNavIcon(item.tag);
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={closeMenu}
                    className={cn(
                      "flex items-center gap-3 rounded-lg p-3 text-sm font-medium transition-all duration-200 hover:text-primary hover:scale-[1.02] group",
                      location.pathname === item.path && "text-primary font-medium bg-primary/5"
                    )}
                  >
                    <Icon className="h-4 w-4 transition-transform group-hover:scale-110" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ApiDropdownMenu({ group, location }: { group: NavigationGroup; location: ReturnType<typeof useLocation> }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isSticky: globalSticky, graceMs } = useGraceConfig();

  const hasPartsHierarchy = group.children.some(c => c.title === "Shop Marine Parts");
  const nonPartsChildren = hasPartsHierarchy ? group.children.filter(c => c.title !== "Shop Marine Parts") : group.children;
  const isSticky = globalSticky && hasPartsHierarchy;

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setHasInteracted(false);
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (isSticky && hasInteracted) return;
    timeoutRef.current = setTimeout(() => closeMenu(), graceMs);
  };

  useEffect(() => {
    if (!isSticky || !isOpen || !hasInteracted) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) closeMenu();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isSticky, isOpen, hasInteracted, closeMenu]);

  return (
    <div ref={containerRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button className="flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors duration-200 hover:text-primary rounded-md">
        {group.title}
        <ChevronDown className={cn("h-3 w-3 transition-transform duration-200", isOpen && "rotate-180")} />
      </button>
      {isOpen && (
        <div
          className="absolute top-full left-0 bg-card backdrop-blur-sm shadow-lg border border-border/20 rounded-lg z-50 animate-fade-in"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {hasPartsHierarchy ? (
            <>
              <PartsHierarchyMenu onClose={closeMenu} onInteraction={() => setHasInteracted(true)} />
              {nonPartsChildren.length > 0 && (
                <>
                  <Separator />
                  <div className="p-2">
                    {nonPartsChildren.map((child) => {
                      const Icon = child.icon;
                      return (
                        <Link
                          key={child.title}
                          to={child.url}
                          onClick={closeMenu}
                          className={cn(
                            "flex items-center gap-3 rounded-lg p-3 text-sm font-medium transition-all duration-200 hover:text-primary hover:scale-[1.02] group",
                            location.pathname === child.url && "text-primary font-medium bg-primary/5"
                          )}
                        >
                          <Icon className="h-4 w-4 transition-transform group-hover:scale-110" />
                          {child.title}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="p-2 w-64">
              {group.children.map((child) => {
                const Icon = child.icon;
                return (
                  <Link
                    key={child.title}
                    to={child.url}
                    onClick={closeMenu}
                    className={cn(
                      "flex items-center gap-3 rounded-lg p-3 text-sm font-medium transition-all duration-200 hover:text-primary hover:scale-[1.02] group",
                      location.pathname === child.url && "text-primary font-medium bg-primary/5"
                    )}
                  >
                    <Icon className="h-4 w-4 transition-transform group-hover:scale-110" />
                    {child.title}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MainNavigation() {
  const location = useLocation();
  const { apiGroups, mockItems, useMock, isLoading } = useMainNavigation();

  if (isLoading || (!useMock && !apiGroups)) {
    return (
      <nav className="hidden md:flex items-center space-x-2 gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-20 rounded-md" />
        ))}
      </nav>
    );
  }

  if (!useMock) {
    return (
      <nav className="hidden md:flex items-center space-x-2">
        {apiGroups.map((group) =>
          group.type === "link" ? (
            <Link
              key={group.title}
              to={group.url || "#"}
              className={cn(
                "px-4 py-2 text-sm font-medium transition-colors duration-200 hover:text-primary rounded-md",
                location.pathname === group.url && "text-primary font-medium"
              )}
            >
              {group.title}
            </Link>
          ) : (
            <ApiDropdownMenu key={group.title} group={group} location={location} />
          )
        )}
      </nav>
    );
  }

  return (
    <nav className="hidden md:flex items-center space-x-2">
      {mockItems.map((item) =>
        item.type === "link" ? (
          <Link
            key={item.name}
            to={item.path!}
            className={cn(
              "px-4 py-2 text-sm font-medium transition-colors duration-200 hover:text-primary rounded-md",
              location.pathname === item.path && "text-primary font-medium"
            )}
          >
            {item.name}
          </Link>
        ) : (
          <DropdownMenu
            key={item.name}
            title={item.name}
            items={item.items as any}
            location={location}
            isPartsMenu={item.isPartsMenu}
          />
        )
      )}
    </nav>
  );
}
